import { isRecordLayoutDefinition, type RecordLayoutDefinition } from '@/composables/recordLayout/recordLayoutDefinition';

/**
 * Talks to `/api/record_layout` (tabasco#825) and keeps "Just me" saves in order.
 *
 * - A save waits a short pause, so dragging a field around sends one request,
 *   not one per move.
 * - A newer save cancels the one before it, so an older answer can never land
 *   after a newer one.
 * - Reset cancels any save still waiting, and waits for one already sent before
 *   it deletes, so a late save can never undo a Reset.
 */

export interface RecordLayoutPair {
    mine: RecordLayoutDefinition | null;
    default: RecordLayoutDefinition | null;
}

/** The three HTTP calls the client needs, so tests can stand in for axios. */
export interface RecordLayoutTransport {
    get(entity: string): Promise<{ mine: unknown; default: unknown }>;
    putMine(entity: string, definition: RecordLayoutDefinition, signal: AbortSignal): Promise<void>;
    deleteMine(entity: string): Promise<void>;
    putDefault(entity: string, definition: RecordLayoutDefinition): Promise<void>;
}

export const SAVE_DELAY_MS = 400;

const RESOURCE_BASE = '/api/record_layout';

/** The real transport, over the app's authenticated HTTP client. */
export async function httpRecordLayoutTransport(): Promise<RecordLayoutTransport> {
    const { default: http } = await import('@/services/http');
    const path = (entity: string) => `${RESOURCE_BASE}/${encodeURIComponent(entity)}`;
    return {
        async get(entity) {
            const response = await http.get<{ mine: unknown; default: unknown }>(RESOURCE_BASE, { params: { entity } });
            return response.data;
        },
        async putMine(entity, definition, signal) {
            await http.put(path(entity), { definition }, { signal });
        },
        async deleteMine(entity) {
            await http.delete(path(entity));
        },
        async putDefault(entity, definition) {
            await http.put(`${path(entity)}/default`, { definition });
        },
    };
}

/** One record type's save state: the save waiting out its pause, and the one on the wire. */
interface PendingSaves {
    timer: ReturnType<typeof setTimeout> | null;
    resolve: (() => void) | null;
    inFlight: { controller: AbortController; done: Promise<unknown> } | null;
}

/**
 * Pending saves are tracked per record type. One client serves the whole app,
 * so a single shared slot let a Zones change cancel a Players change still
 * waiting out its pause, and the Players change was never sent.
 */
export function createRecordLayoutClient(transport: RecordLayoutTransport, delayMs: number = SAVE_DELAY_MS) {
    const savesByEntity = new Map<string, PendingSaves>();

    function savesFor(entity: string): PendingSaves {
        let saves = savesByEntity.get(entity);
        if (!saves) {
            saves = { timer: null, resolve: null, inFlight: null };
            savesByEntity.set(entity, saves);
        }
        return saves;
    }

    async function load(entity: string): Promise<RecordLayoutPair> {
        const answer = await transport.get(entity);
        return {
            mine: isRecordLayoutDefinition(answer.mine) ? answer.mine : null,
            default: isRecordLayoutDefinition(answer.default) ? answer.default : null,
        };
    }

    function cancelPending(saves: PendingSaves) {
        if (saves.timer !== null) {
            clearTimeout(saves.timer);
            saves.timer = null;
            saves.resolve?.();
            saves.resolve = null;
        }
    }

    /** Saves after a pause. Resolves when this save is sent and answered, or replaced by a newer one for the same record type. */
    function saveMine(entity: string, definition: RecordLayoutDefinition): Promise<void> {
        const saves = savesFor(entity);
        cancelPending(saves);
        return new Promise<void>((resolve, reject) => {
            saves.resolve = resolve;
            saves.timer = setTimeout(() => {
                saves.timer = null;
                saves.resolve = null;
                saves.inFlight?.controller.abort();
                const controller = new AbortController();
                const done = transport.putMine(entity, definition, controller.signal);
                saves.inFlight = { controller, done };
                done.then(
                    () => resolve(),
                    (error: unknown) => (controller.signal.aborted ? resolve() : reject(error)),
                ).finally(() => {
                    if (saves.inFlight?.controller === controller) {
                        saves.inFlight = null;
                    }
                });
            }, delayMs);
        });
    }

    async function resetMine(entity: string): Promise<void> {
        const saves = savesFor(entity);
        cancelPending(saves);
        if (saves.inFlight) {
            await saves.inFlight.done.catch(() => undefined);
        }
        await transport.deleteMine(entity);
    }

    function saveDefault(entity: string, definition: RecordLayoutDefinition): Promise<void> {
        return transport.putDefault(entity, definition);
    }

    return { load, saveMine, resetMine, saveDefault };
}
